import { useEffect, useRef } from "react";
import {
  AlertTriangle,
  Archive,
  ArchiveRestore,
  CalendarCheck,
  CalendarPlus,
  CalendarX,
  CheckCheck,
  ClipboardCheck,
  ClipboardPen,
  ClipboardX,
  DatabaseBackup,
  Trash2,
  FileCheck,
  FilePen,
  FileSignature,
  ListTodo,
  MailWarning,
  MailX,
  FileX,
  KeyRound,
  Receipt,
  ReceiptText,
  ShieldCheck,
  UserPlus,
  UserX,
  X,
} from "lucide-react";
import { Badge } from "../../components/Badge";
import { useFocusTrap } from "../../lib/keyboard";
import { Button } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import { formatTaipeiDateTime } from "../../lib/datetime";
import { NOTIFICATION_KIND_LABEL } from "../../api/types";
import type { NotificationItem, NotificationKind } from "../../api/types";

// 從右側滑出的通知面板。
//
// --------------------------------------------------------------------------
// 一律是懸浮的,**任何寬度都一樣**(業主 2026-09-17)
// --------------------------------------------------------------------------
// > 右上角 通知鈴鐺 點了做會有做分割 取消 改成側邊右懸浮
// > 高度就是一整頁 寬度跟現在一樣
// > 只是我想要的是不要壓縮版面 因為他不太可能會常駐
//
// 這裡本來有兩種模式:≥1536px 常駐在右邊(推開內容、預設展開),窄螢幕才是
// 蓋上來的抽屜。業主把常駐那一半取消了,理由是**面板不會一直開著** ——
// 為了一個偶爾看一眼的東西永遠讓出 22rem,報價單編輯器每天都少一截,
// 那正是他先前抱怨過的「欄位資料都被壓縮 很醜」。
//
// 所以只剩一種模式,也就不再有「看起來是 modal、行為不是 modal」的中間態:
// 遮罩是 fixed inset-0,蓋住整頁、吃掉所有點擊 —— 那後面那一整頁就是不能用的,
// 無障礙的規矩也就與對話框完全相同:aria-modal、焦點陷阱、鎖住背景捲動,
// 三件缺一不可。少了它們,鍵盤使用者從面板最後一顆按鈕再按一次 Tab,焦點就
// 落到遮罩底下的頁面元素上:看得到焦點環、點下去卻只會關掉面板;螢幕閱讀器
// 更是完全不知道後面那一頁現在不可用。側邊欄抽屜(AppShell)三件都做了,
// 理由一模一樣。
//
// 出路有三條:Esc、右上角的關閉鈕,以及再按一次鈴鐺。
//
// --------------------------------------------------------------------------
// 未讀與已讀不可以只靠底色
// --------------------------------------------------------------------------
// 每一列都掛一枚寫著「未讀」或「已讀」的徽章。把樣式整個拿掉,
// 兩者仍然分得出來 —— 顏色與粗體只是把它講得更快,不是唯一的差別。

/** 面板寬度。窄螢幕上留一點空隙,讓人看得出後面還有頁面。 */
const PANEL_WIDTH = "w-[min(22rem,90vw)]";

/**
 * 每一種通知的圖示。
 *
 * 圖示一律 aria-hidden:它是幫忙掃視的,不是資訊來源。
 * 同一列旁邊會寫出 `NOTIFICATION_KIND_LABEL` 的文字,看不到圖示的人不會少知道任何事。
 */
function KindIcon({ kind }: { kind: NotificationKind }) {
  const size = 16;
  switch (kind) {
    case "LEAVE_SUBMITTED":
      return <CalendarPlus aria-hidden="true" size={size} />;
    case "LEAVE_DECIDED":
      return <CalendarCheck aria-hidden="true" size={size} />;
    case "LEAVE_REVOKED":
      return <CalendarX aria-hidden="true" size={size} />;
    case "QUOTE_ASSIGNED":
      return <UserPlus aria-hidden="true" size={size} />;
    case "QUOTE_STATUS":
      return <FileCheck aria-hidden="true" size={size} />;
    case "QUOTE_EDITED":
      return <FilePen aria-hidden="true" size={size} />;
    case "QUOTE_ARCHIVED":
      return <Archive aria-hidden="true" size={size} />;
    case "QUOTE_UNARCHIVED":
      return <ArchiveRestore aria-hidden="true" size={size} />;
    case "QUOTE_DELETED":
      return <FileX aria-hidden="true" size={size} />;
    case "INVOICE_REQUESTED":
      return <Receipt aria-hidden="true" size={size} />;
    case "INVOICE_REQUEST_CANCELLED":
      return <ReceiptText aria-hidden="true" size={size} />;
    case "QUOTE_SIGNED_BACK":
      return <FileSignature aria-hidden="true" size={size} />;
    case "TODO_ASSIGNED":
      return <ListTodo aria-hidden="true" size={size} />;
    case "TODO_DONE":
      return <CheckCheck aria-hidden="true" size={size} />;
    case "ACCOUNT_DISABLED":
      return <UserX aria-hidden="true" size={size} />;
    case "ACCOUNT_PASSWORD_RESET":
      return <KeyRound aria-hidden="true" size={size} />;
    // 個人信箱(第二階段):兩種都連到 /mailbox(連結由後端給)
    case "MAIL_SEND_FAILED":
      return <MailX aria-hidden="true" size={size} />;
    case "MAIL_FOLLOWUP_DUE":
      return <MailWarning aria-hidden="true" size={size} />;
    // 每日備份失敗 / 異地備份還沒設定(收件人是老闆,沒有連結)
    case "BACKUP_FAILED":
      return <DatabaseBackup aria-hidden="true" size={size} />;
    // 製單(2026-10-08):送覆核 / 通過 / 退回,連到那張工單
    case "WORK_ORDER_REVIEW_REQUESTED":
      return <ClipboardPen aria-hidden="true" size={size} />;
    case "WORK_ORDER_APPROVED":
      return <ClipboardCheck aria-hidden="true" size={size} />;
    case "WORK_ORDER_RETURNED":
      return <ClipboardX aria-hidden="true" size={size} />;
    default:
      return <ShieldCheck aria-hidden="true" size={size} />;
  }
}

export interface NotificationPanelProps {
  open: boolean;
  items: NotificationItem[];
  /**
   * 伺服器說現在有幾則未讀。**「全部標為已讀」要不要停用看它,不看 items。**
   *
   * 清單有可能停在舊的那一份(面板整天開著,而輪詢先拿到新的數字),這時用
   * items 去算,畫面會同時宣稱「有 3 則未讀」和「沒有東西可以標為已讀」。
   */
  unreadCount: number;
  loading: boolean;
  /** 清單這一趟讀失敗了。**不可以當成「沒有通知」**顯示。 */
  failed: boolean;
  onClose: () => void;
  onSelect: (item: NotificationItem) => void;
  onMarkAllRead: () => void;
  /**
   * 把讀過的清掉(業主 2026-09-17)。
   *
   * 通知欄是「最近發生了什麼」,不是稽核紀錄 —— 真正要留痕的事情在
   * 報價單狀態紀錄與稽核紀錄裡,那兩個地方誰都刪不掉。
   */
  onClearRead: () => void;
  /** 有幾則是讀過的。0 的時候那顆鈕按不下去 —— 按了才知道沒東西可清,不如先說。 */
  readCount: number;
}

export function NotificationPanel({
  open,
  items,
  unreadCount,
  loading,
  failed,
  onClose,
  onSelect,
  onMarkAllRead,
  onClearRead,
  readCount,
}: NotificationPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  // 開著就是蓋住整頁,沒有第二種模式 —— 所以焦點一律困在裡面。
  const modal = open;
  useFocusTrap(panelRef, modal);

  // 蓋著整頁時鎖住背景捲動:底下那一頁現在不能用,還跟著捲只會讓人以為點得到。
  useEffect(() => {
    if (!modal || typeof document === "undefined") return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [modal]);

  // 打開時把焦點移進面板,鍵盤使用者不必自己 Tab 一輪才進得來。
  // 只在「從關到開」那一下做,之後點清單的焦點不會被搶回來。
  useEffect(() => {
    if (!open) return;
    const node = panelRef.current;
    if (node && !node.contains(document.activeElement)) node.focus();
  }, [open]);

  if (!open) return null;

  return (
    <>
      {/* 遮罩**每一種寬度都在**:面板是懸浮的,底下那一頁不該點得到。 */}
      <div
        aria-hidden="true"
        data-testid="nq-notifications-backdrop"
        onClick={onClose}
        className="fixed inset-0 z-30 bg-[rgba(28,25,23,0.42)]"
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-label="通知"
        // 外框(AppShell)靠這個標記判斷「焦點在不在面板裡」,
        // 決定 Esc 該不該由它來關、關完要不要把焦點還給鈴鐺
        data-nq-panel="notifications"
        // 遮罩蓋著整頁,後面確實不可用 —— 這個屬性要在
        aria-modal={modal ? true : undefined}
        tabIndex={-1}
        className={
          `fixed inset-y-0 right-0 z-40 flex ${PANEL_WIDTH} flex-col border-l ` +
          "border-[var(--border)] bg-[var(--surface)] shadow-[var(--shadow)]"
        }
      >
        {/* 用 div 不用 header:header 在這裡會變成第二個 banner 地標,
            頂列那一個才是真正的 banner。 */}
        <div className="flex items-center gap-2 border-b border-[var(--border)] px-3 py-2">
          <h2 className="m-0 flex-1 text-sm font-semibold text-[var(--text)]">通知</h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={onMarkAllRead}
            disabled={unreadCount === 0}
            icon={<CheckCheck aria-hidden="true" size={16} />}
          >
            全部標為已讀
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearRead}
            disabled={readCount === 0}
            title={readCount === 0 ? "沒有讀過的通知可以清" : undefined}
            icon={<Trash2 aria-hidden="true" size={16} />}
          >
            刪除已讀
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            aria-label="關閉通知"
            icon={<X aria-hidden="true" size={16} />}
          />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {failed ? (
            // 讀不到就寫讀不到。顯示「目前沒有通知」是在宣稱一件不知道真假的事:
            // 使用者會以為沒人找他,而實際上可能有三張假單等著簽。
            <p
              role="status"
              className="m-0 flex items-start gap-2 px-3 py-6 text-sm text-[var(--text-2)]"
            >
              <AlertTriangle aria-hidden="true" size={16} className="mt-0.5 shrink-0 text-[var(--warn)]" />
              通知暫時讀不到,稍後再開一次。其他功能都不受影響。
            </p>
          ) : loading && items.length === 0 ? (
            <p className="m-0 px-3 py-6 text-sm text-[var(--text-3)]">載入中…</p>
          ) : items.length === 0 ? (
            <EmptyState
              size="sm"
              className="m-3"
              title="目前沒有通知"
              description="有人送出假單,或你負責的報價單有異動時,會出現在這裡。"
            />
          ) : (
            <ul className="m-0 list-none p-0">
              {items.map((item) => {
                const unread = item.read_at === null;
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => onSelect(item)}
                      className={
                        "flex w-full items-start gap-2 border-b border-[var(--border)] px-3 py-2 " +
                        "text-left hover:bg-[var(--surface-2)] " +
                        (unread ? "bg-[var(--surface)]" : "bg-[var(--surface-2)]")
                      }
                    >
                      <span className="mt-0.5 shrink-0 text-[var(--text-3)]">
                        <KindIcon kind={item.kind} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span
                          className={
                            "block text-sm " +
                            (unread ? "font-semibold text-[var(--text)]" : "text-[var(--text-2)]")
                          }
                        >
                          {item.title}
                        </span>
                        {item.body ? (
                          <span className="mt-0.5 block text-xs text-[var(--text-2)]">{item.body}</span>
                        ) : null}
                        <span className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-[var(--text-3)]">
                          <Badge tone={unread ? "accent" : "neutral"}>{unread ? "未讀" : "已讀"}</Badge>
                          <span>{NOTIFICATION_KIND_LABEL[item.kind]}</span>
                          <span className="tabular">{formatTaipeiDateTime(item.created_at)}</span>
                          {/* 後端把 30 分鐘內的同一件事併成一則。「只改一次」與
                              「改了十次」在畫面上要分得出來,不能只寫在 body 裡。 */}
                          {item.repeat_count > 1 ? <span>共 {item.repeat_count} 次</span> : null}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </>
  );
}
